import { Box } from '@mantine/core';
import { forwardRef, useState, useRef, useCallback } from 'react';
import { useReducedMotion } from '../hooks';

export interface MagneticProps {
  children: React.ReactNode;
  strength?: number;
  radius?: number;
  duration?: number;
}

const Magnetic = forwardRef<HTMLDivElement, MagneticProps>(
  (
    {
      children,
      strength = 0.3,
      radius = 100,
      duration = 150,
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();
    const containerRef = useRef<HTMLDivElement | null>(null) as React.MutableRefObject<HTMLDivElement | null>;
    const [offset, setOffset] = useState({ x: 0, y: 0 });

    const handleMouseMove = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (reducedMotion || !containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const distanceX = e.clientX - centerX;
        const distanceY = e.clientY - centerY;
        const distance = Math.sqrt(distanceX ** 2 + distanceY ** 2);

        if (distance < radius) {
          const factor = (1 - distance / radius) * strength;
          setOffset({
            x: distanceX * factor,
            y: distanceY * factor,
          });
        }
      },
      [radius, strength, reducedMotion]
    );

    const handleMouseLeave = useCallback(() => {
      setOffset({ x: 0, y: 0 });
    }, []);

    return (
      <Box
        ref={(node) => {
          containerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{
          transform: `translate(${offset.x}px, ${offset.y}px)`,
          transition: reducedMotion
            ? 'none'
            : offset.x === 0 && offset.y === 0
              ? `transform ${duration}ms ease-out`
              : 'none',
          display: 'inline-block',
        }}
      >
        {children}
      </Box>
    );
  }
);

Magnetic.displayName = 'Magnetic';

export default Magnetic;
